import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '@/components/common/PageHeader';
import { EmptyState } from '@/components/common/EmptyState';
import { Modal } from '@/components/ui/Modal';
import { useToast } from '@/hooks/useToast';
import { useAuth } from '@/hooks/useAuth';
import { WarrantyPresetApi } from '@/api';
import { Plus, Edit, Trash2, X } from 'lucide-react';

export default function WarrantyPresets() {
  const { push } = useToast();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    machine_months: 0,
    components_covered: '',
    addons: [] as { component: string; months: number }[],
    conditions: '',
    is_default: false,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const presetsQuery = useQuery({
    queryKey: ['warrantyPresets'],
    queryFn: () => WarrantyPresetApi.list(),
  });

  const createMutation = useMutation({
    mutationFn: (data: any) => WarrantyPresetApi.create(data),
    onSuccess: () => {
      push({ tone: 'success', title: 'Warranty preset created' });
      setModalOpen(false);
      resetForm();
      queryClient.invalidateQueries({ queryKey: ['warrantyPresets'] });
    },
    onError: (err: any) => {
      push({ tone: 'error', title: 'Failed to create preset', body: err.message });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: any) => WarrantyPresetApi.update(editId!, data),
    onSuccess: () => {
      push({ tone: 'success', title: 'Warranty preset updated' });
      setModalOpen(false);
      resetForm();
      queryClient.invalidateQueries({ queryKey: ['warrantyPresets'] });
    },
    onError: (err: any) => {
      push({ tone: 'error', title: 'Failed to update preset', body: err.message });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => WarrantyPresetApi.remove(id),
    onSuccess: () => {
      push({ tone: 'success', title: 'Warranty preset deleted' });
      queryClient.invalidateQueries({ queryKey: ['warrantyPresets'] });
    },
    onError: (err: any) => {
      push({ tone: 'error', title: 'Failed to delete preset', body: err.message });
    },
  });

  const resetForm = () => {
    setFormData({
      name: '',
      machine_months: 0,
      components_covered: '',
      addons: [],
      conditions: '',
      is_default: false,
    });
    setEditId(null);
    setErrors({});
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.name) newErrors.name = 'Name is required';
    if (formData.machine_months <= 0) newErrors.machine_months = 'Must be > 0';
    if (!formData.components_covered) newErrors.components_covered = 'Components covered required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleOpenModal = (preset?: any) => {
    if (preset) {
      setFormData({
        name: preset.name,
        machine_months: preset.machine_months,
        components_covered: preset.components_covered,
        addons: preset.addons_json || [],
        conditions: preset.conditions,
        is_default: preset.is_default,
      });
      setEditId(preset.id);
    } else {
      resetForm();
    }
    setModalOpen(true);
  };

  const handleSave = () => {
    if (!validateForm()) return;
    const data = {
      name: formData.name,
      machine_months: Number(formData.machine_months),
      components_covered: formData.components_covered,
      addons_json: formData.addons,
      conditions: formData.conditions,
      is_default: formData.is_default,
    };

    if (editId) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  return (
    <div className="space-y-5">
      <PageHeader
        title="Warranty Presets"
        subtitle={isAdmin ? 'Define standard warranty terms' : 'View standard warranty terms'}
        actions={
          isAdmin ? (
            <button
              className="btn btn-primary gap-2"
              onClick={() => handleOpenModal()}
            >
              <Plus size={16} /> Add Preset
            </button>
          ) : undefined
        }
      />

      {/* Table */}
      {presetsQuery.isLoading ? (
        <div className="text-center py-12 text-muted">Loading…</div>
      ) : !presetsQuery.data || presetsQuery.data.length === 0 ? (
        <EmptyState
          title="No warranty presets"
          body={isAdmin ? 'Create your first warranty preset.' : 'No warranty presets have been added yet.'}
          action={
            isAdmin ? (
              <button
                className="btn btn-primary"
                onClick={() => handleOpenModal()}
              >
                Add Preset
              </button>
            ) : undefined
          }
        />
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="table-clean">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Machine Months</th>
                  <th>Components</th>
                  <th>Addons</th>
                  <th>Default</th>
                  {isAdmin && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {presetsQuery.data.map((w: any) => (
                  <tr key={w.id}>
                    <td className="font-medium">{w.name}</td>
                    <td className="text-sm">{w.machine_months}</td>
                    <td className="text-sm text-muted truncate max-w-xs">{w.components_covered}</td>
                    <td className="text-sm text-muted">{w.addons_json?.length || 0} addons</td>
                    <td>{w.is_default ? '✓' : '-'}</td>
                    {isAdmin && (
                      <td className="flex gap-1">
                        <button
                          className="btn btn-sm btn-ghost"
                          onClick={() => handleOpenModal(w)}
                        >
                          <Edit size={14} />
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => deleteMutation.mutate(w.id)}
                          disabled={deleteMutation.isPending}
                        >
                          <Trash2 size={14} />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal */}
      <Modal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          resetForm();
        }}
        title={editId ? 'Edit Warranty Preset' : 'Add Warranty Preset'}
        footer={
          <>
            <button
              className="btn btn-secondary"
              onClick={() => {
                setModalOpen(false);
                resetForm();
              }}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              Save
            </button>
          </>
        }
      >
        <div className="space-y-4 max-h-96 overflow-y-auto">
          <div>
            <label className="label">Name *</label>
            <input
              type="text"
              className={`input mt-1 ${errors.name ? 'input-error' : ''}`}
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g. Standard 12M"
            />
            {errors.name && <div className="text-xs text-rdx mt-1">{errors.name}</div>}
          </div>

          <div>
            <label className="label">Machine Warranty (Months) *</label>
            <input
              type="number"
              className={`input mt-1 ${errors.machine_months ? 'input-error' : ''}`}
              value={formData.machine_months}
              onChange={(e) => setFormData({ ...formData, machine_months: Number(e.target.value) })}
              min={1}
            />
            {errors.machine_months && <div className="text-xs text-rdx mt-1">{errors.machine_months}</div>}
          </div>

          <div>
            <label className="label">Components Covered *</label>
            <textarea
              className={`input mt-1 ${errors.components_covered ? 'input-error' : ''}`}
              value={formData.components_covered}
              onChange={(e) => setFormData({ ...formData, components_covered: e.target.value })}
              rows={3}
              placeholder="List all components covered..."
            />
            {errors.components_covered && <div className="text-xs text-rdx mt-1">{errors.components_covered}</div>}
          </div>

          <div className="bg-amber-50 p-2 rounded text-xs text-amber-700">
            Note: Printheads (Ricoh Gen6 MH5420/MH5421) are explicitly excluded from warranty coverage per standard policy.
          </div>

          <div className="border-t border-line pt-3">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-medium">Warranty Addons</h4>
              <button
                className="btn btn-sm btn-secondary"
                onClick={() =>
                  setFormData({
                    ...formData,
                    addons: [...formData.addons, { component: '', months: 0 }],
                  })
                }
              >
                + Add
              </button>
            </div>

            <div className="space-y-2">
              {formData.addons.map((addon, idx) => (
                <div key={idx} className="flex gap-2 items-end">
                  <div className="flex-1">
                    <label className="label text-xs">Component</label>
                    <input
                      type="text"
                      className="input mt-1 text-sm"
                      value={addon.component}
                      onChange={(e) => {
                        const newAddons = [...formData.addons];
                        newAddons[idx].component = e.target.value;
                        setFormData({ ...formData, addons: newAddons });
                      }}
                      placeholder="e.g. Glass Bed"
                    />
                  </div>
                  <div className="w-24">
                    <label className="label text-xs">Months</label>
                    <input
                      type="number"
                      className="input mt-1 text-sm"
                      value={addon.months}
                      onChange={(e) => {
                        const newAddons = [...formData.addons];
                        newAddons[idx].months = Number(e.target.value);
                        setFormData({ ...formData, addons: newAddons });
                      }}
                      min={0}
                    />
                  </div>
                  <button
                    className="btn btn-sm btn-danger mb-0"
                    onClick={() =>
                      setFormData({
                        ...formData,
                        addons: formData.addons.filter((_, i) => i !== idx),
                      })
                    }
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div>
            <label className="label">Terms & Conditions</label>
            <textarea
              className="input mt-1"
              value={formData.conditions}
              onChange={(e) => setFormData({ ...formData, conditions: e.target.value })}
              rows={3}
              placeholder="Special conditions, limitations, etc."
            />
          </div>

          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              className="w-4 h-4"
              checked={formData.is_default}
              onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
            />
            <span className="text-sm">Set as default</span>
          </label>
        </div>
      </Modal>
    </div>
  );
}
